import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { notesApi, filesApi, organizationsApi } from "@/lib/api";
import ContactPicker from "@/components/ContactPicker";
import { toast } from "sonner";
import { Paperclip, X, Building2 } from "lucide-react";

const empty = { title: "", content: "", entry_type: "note", project_id: "", date: new Date().toISOString().slice(0,10),
  decision_reason: "", responsible_contact_id: "", attachment_urls: [], attachment_file_ids: [] };

export default function NoteDialog({ open, setOpen, note, entityId, projects, contacts, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  useEffect(() => { setForm(note ? { ...empty, ...note, attachment_urls: note.attachment_urls || [], attachment_file_ids: note.attachment_file_ids || [] } : empty); }, [note, open]);
  const set = (k,v) => setForm(f => ({ ...f, [k]: v }));

  const uploadFile = async (f) => {
    setUploading(true);
    try {
      const r = await filesApi.upload(f);
      set("attachment_file_ids", [...form.attachment_file_ids, r.data.id]);
      toast.success("File uploaded");
    } catch(e){ toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) return toast.error("Title and content required");
    setSaving(true);
    try {
      const payload = { ...form, entity_id: entityId };
      if (!payload.project_id) payload.project_id = null;
      if (!payload.responsible_contact_id) payload.responsible_contact_id = null;
      if (note?.id) await notesApi.update(note.id, payload);
      else await notesApi.create(payload);
      toast.success("Saved"); onSaved?.();
    } catch(e){ toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#151A23] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="note-dialog">
        <DialogHeader><DialogTitle className="font-display">{note?.id ? "Edit entry" : "New entry"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Type</Label>
            <Select value={form.entry_type} onValueChange={v=>set("entry_type", v)}><SelectTrigger className="mt-1.5 bg-white/5 border-white/10"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">{["note","idea","meeting_notes","decision","direction_change"].map(s=><SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-white/60 text-xs uppercase tracking-wider">Date</Label><Input type="date" value={form.date || ""} onChange={e=>set("date", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/></div>
          <div className="col-span-2"><Label className="text-white/60 text-xs uppercase tracking-wider">Title</Label><Input data-testid="note-title-input" value={form.title} onChange={e=>set("title", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/></div>
          <div className="col-span-2"><Label className="text-white/60 text-xs uppercase tracking-wider">Content</Label><Textarea rows={6} value={form.content} onChange={e=>set("content", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/></div>
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Project</Label>
            <Select value={form.project_id || "__none"} onValueChange={v=>set("project_id", v === "__none" ? "" : v)}>
              <SelectTrigger className="mt-1.5 bg-white/5 border-white/10"><SelectValue placeholder="None"/></SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">
                <SelectItem value="__none">None</SelectItem>
                {projects?.map(p=><SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Responsible</Label>
            <div className="mt-1.5"><ContactPicker value={form.responsible_contact_id} onChange={(id)=>set("responsible_contact_id", id)} entityId={entityId} projectId={form.project_id}/></div>
          </div>
          {form.entry_type === "decision" && (
            <div className="col-span-2"><Label className="text-white/60 text-xs uppercase tracking-wider">Decision reason</Label><Textarea rows={2} value={form.decision_reason || ""} onChange={e=>set("decision_reason", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/></div>
          )}
          <div className="col-span-2">
            <Label className="text-white/60 text-xs uppercase tracking-wider">Attachments</Label>
            <div className="mt-1.5 space-y-2">
              {form.attachment_file_ids.map((fid) => (
                <div key={fid} className="flex items-center justify-between p-2 rounded bg-white/5 border border-white/10 text-sm">
                  <div className="flex items-center gap-2"><Paperclip className="w-3.5 h-3.5"/> File {fid.slice(0,8)}…</div>
                  <button type="button" onClick={() => set("attachment_file_ids", form.attachment_file_ids.filter(x => x !== fid))} className="p-1 hover:bg-white/10 rounded"><X className="w-3.5 h-3.5"/></button>
                </div>
              ))}
              {form.attachment_urls.map((u, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded bg-white/5 border border-white/10 text-sm">
                  <a href={u} target="_blank" rel="noreferrer" className="truncate text-blue-400 hover:underline">{u}</a>
                  <button type="button" onClick={() => set("attachment_urls", form.attachment_urls.filter((_,j)=>j!==i))} className="p-1 hover:bg-white/10 rounded"><X className="w-3.5 h-3.5"/></button>
                </div>
              ))}
              <label className="cursor-pointer block">
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}/>
                <div className="px-3 py-2 rounded bg-white/5 border border-white/10 text-sm text-white/70 hover:bg-white/10 text-center">{uploading ? "Uploading…" : "+ Upload file"}</div>
              </label>
              <div className="flex gap-2">
                <Input placeholder="Add external URL" value={newUrl} onChange={e=>setNewUrl(e.target.value)} className="bg-white/5 border-white/10"/>
                <Button variant="ghost" onClick={() => { if(newUrl.trim()){ set("attachment_urls", [...form.attachment_urls, newUrl.trim()]); setNewUrl(""); } }} className="text-white/70 hover:text-white hover:bg-white/10">Add</Button>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)} className="text-white/70 hover:text-white hover:bg-white/10">Cancel</Button>
          {note?.id && !(note.tags || []).some(t => (t || "").startsWith("org:")) && (
            <Button variant="ghost" data-testid="convert-note-to-org-btn"
              onClick={async () => {
                if (!window.confirm(`Convert "${note.title}" into a canonical Organization?`)) return;
                try {
                  const r = await organizationsApi.convertNote(note.id, {
                    name: note.title, summary: note.content,
                    organization_type: "Other",
                    relationship_status: "Potential Fit",
                  });
                  toast.success(r.data.created ? "Organization created" : "Merged into existing organization");
                  setOpen(false);
                  onSaved?.();
                } catch (e) { toast.error(e.response?.data?.detail || "Convert failed"); }
              }}
              className="text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10">
              <Building2 className="w-4 h-4 mr-1"/>Convert to Organization
            </Button>
          )}
          <Button onClick={save} disabled={saving} data-testid="note-save-btn" className="rounded-full bg-white text-black hover:bg-white/90">{saving?"Saving…":"Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
